import { Component, OnInit } from '@angular/core';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { FormControl, FormGroup, FormsModule, Validators } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { PasswordModule } from 'primeng/password';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { Router } from '@angular/router';
import { AuthService, LoginResponse } from '../../core/services/auth';
import { NetworkStatusService } from '../../core/services/network-status';
import { UserService } from '../../core/services/user.service';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';

interface ErrorMapping {
  [key: string]: string;
}

// Extend the LoginResponse interface to include accessToken
interface ExtendedLoginResponse extends LoginResponse {
  accessToken?: string;
}

@Component({
  selector: 'app-sign-up',
  imports: [
    InputGroupModule, 
    InputGroupAddonModule, 
    FormsModule, 
    InputTextModule, 
    CommonModule, 
    ReactiveFormsModule, 
    ButtonModule, 
    PasswordModule,
    ToastModule
  ],
  providers: [MessageService],
  templateUrl: './sign-up.html',
  styleUrl: './sign-up.scss'
})
export class SignUp implements OnInit {
  loading: boolean = false;
  isOnline: boolean = true;
  roleId: string = '68938dbd84372f332e70cf2a';
  recaptchaSiteKey: string = environment.recaptchaSiteKey;
  signUpForm: FormGroup = new FormGroup({
    companyName: new FormControl('', [Validators.required]),
    email: new FormControl('', [Validators.required, Validators.email]),
    firstName: new FormControl('', [Validators.required]),
    lastName: new FormControl('', [Validators.required]),
  });

  private readonly errorMessages: ErrorMapping = {
    'user not found': 'User not found. Please check your email address.',
    'invalid credentials': 'Invalid credentials. Please check your email and password.',
    'user disabled': 'Your account has been disabled. Please contact support.',
    '0 unknown error': 'Unable to connect to the server. Please check your internet connection and try again.',
    'network error': 'Network error. Please check your internet connection and try again.',
    'timeout': 'Request timed out. Please try again.',
  };

  constructor(
    private authService: AuthService,
    private messageService: MessageService,
    private router: Router,
    private networkStatusService: NetworkStatusService,
    private http: HttpClient,
    private userService: UserService
  ) {}

  ngOnInit() {
    this.networkStatusService.getNetworkStatus().subscribe(status => {
      this.isOnline = status;
      console.log('Network Status:', status ? 'Online' : 'Offline');
      if (!status) {
        this.showMessage('error', 'Offline', 'You are offline. Please check your internet connection.', 5000);
      }
    });
  }

  async executeRecaptcha(): Promise<string | null> {
    return new Promise((resolve) => {
      if (typeof (window as any).grecaptcha === 'undefined') {
        // Wait for reCAPTCHA script to load
        const checkRecaptcha = setInterval(() => {
          if (typeof (window as any).grecaptcha !== 'undefined') {
            clearInterval(checkRecaptcha);
            this.getRecaptchaToken(resolve);
          }
        }, 100);
      } else {
        this.getRecaptchaToken(resolve);
      }
    });
  }

  private getRecaptchaToken(resolve: (token: string | null) => void) {
    if (typeof (window as any).grecaptcha !== 'undefined') {
      (window as any).grecaptcha.ready(() => {
        (window as any).grecaptcha
          .execute(this.recaptchaSiteKey, { action: 'signup' })
          .then((token: string) => {
            resolve(token);
          })
          .catch((error: any) => {
            console.error('reCAPTCHA v3 error:', error);
            resolve(null);
          });
      });
    } else {
      resolve(null);
    }
  }

  async onSubmit() {
    if (!this.signUpForm.valid) {
      this.showMessage('warn', 'Validation Error', 'Please fill in all required fields correctly.', 4000);
      return;
    }

    this.loading = true;
    
    // Execute reCAPTCHA v3
    const recaptchaToken = await this.executeRecaptcha();
    
    if (!recaptchaToken) {
      this.loading = false;
      this.showMessage('error', 'Verification Failed', 'reCAPTCHA verification failed. Please try again.', 4000);
      return;
    }

    const { companyName, email, firstName, lastName } = this.signUpForm.value;

    console.log('Registering user with data:', {
      firstName,
      lastName,
      email,
      roleId: this.roleId,
      companyName
    });
    
    this.userService.registerUser({
      firstName,
      lastName,
      email,
      roleId: this.roleId,
      companyName,
      recaptchaToken
    }).subscribe({
      next: (response) => {
        this.showMessage('success', 'Account Created', 'Your account has been created successfully! You will receive an email invitation.', 8000);
        this.loading = false;
        
        // Redirect to login page after successful registration
        setTimeout(() => {
          this.router.navigate(['/']);
        }, 5000);
      },
      error: (error) => {
        console.error('Error creating account:', error);
        this.showMessage('error', 'Registration Failed', error.error.message, 5000);
        this.loading = false;
      }
    });
  }

  private showMessage(severity: string, summary: string, detail: string, life?: number) {
    this.messageService.add({ 
      severity, 
      summary, 
      detail,
      life: life || 3000
    });
  }
}
